import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { TuiRoot } from '@taiga-ui/core';
import { filter } from 'rxjs';
import { version } from '../../package.json';
import { AuthenticationService } from './modules/authentication/services/authentication.service';
import { DrawerPortalComponent } from './modules/drawer/drawer-portal/drawer-portal.component';
import { LoaderComponent } from './modules/shared/loader/loader.component';
import { LoadingOverlayService } from './services/loading-overlay.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block h-full',
  },
  standalone: true,
  imports: [RouterOutlet, AsyncPipe, TranslocoDirective, TuiRoot, LoaderComponent, DrawerPortalComponent],
})
export class AppComponent implements OnInit {
  private readonly appInsights = inject(ApplicationInsights);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly loadingOverlay = inject(LoadingOverlayService);
  private readonly router = inject(Router);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  isBrowserIE = window.document.documentMode;

  readonly isLoading$ = this.loadingOverlay.isLoading$;

  constructor() {
    if (!this.router.navigated) {
      this.loadingOverlay.show();
    }
    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError,
        ),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.loadingOverlay.hide());
  }

  ngOnInit(): void {
    this.appInsights.loadAppInsights();
    this.appInsights.addTelemetryInitializer((item) => {
      item.tags = item.tags || [];
      item.tags['ai.cloud.role'] = 'web-app';
      item.tags['ai.application.version'] = version;
      const userId = this.authenticationService.user?.id;
      if (userId) {
        item.tags['ai.user.id'] = userId;
      }
    });
    this.appInsights.trackPageView();
  }
}
